/**
 * Positive evidence that a third party rewrote the DOM under React (TES-2160).
 *
 * ## What this is for
 *
 * React #418 ("the server rendered HTML didn't match the client") carries no
 * useful stack: the throwing frame is the React runtime chunk, identical for
 * every cause, so there is nothing in the event itself to attribute it with.
 * TES-1977 fixed the one first-party cause we could name — locale-sensitive
 * catalog counts — and #418 kept firing on /sounds, /sounds/[id] and /guide,
 * with the de-DE probe clean on all three.
 *
 * `scripts/qa/tes2160-translation-hydration-probe.mjs` settles the mechanism:
 * splice a synchronous script in just before `</body>` (so it runs after the
 * SSR markup exists and before Next's async bundle hydrates), wrap each text
 * node in the nested `<font style="vertical-align: inherit;">` pair Chrome
 * Translate uses, and every one of those five routes reports #418 while the
 * un-mutated baseline stays clean. Pre-hydration third-party DOM mutation is a
 * sufficient cause.
 *
 * Sufficient is not the same as attributed, which is what this module is for:
 * it reads the markers a translator or content script *leaves behind*, so a
 * #418 can be tied to one rather than assumed to come from one.
 *
 * ## Why these four markers and not "the page looks translated"
 *
 * Every marker below is something we never emit. That is the same rule
 * `lib/sentry-noise.ts` keeps for its CSP and injected-bundle predicates, and
 * it is what stops this from becoming a blanket "#418 is noise" filter that
 * would swallow a real nondeterministic render. `<font>` is a deprecated
 * element this codebase authors nowhere (only in comments describing this very
 * failure), `translated-ltr`/`translated-rtl` are Google Translate's own class
 * names, `_msttexthash` is Microsoft Translator's stamp, and the guard counters
 * only leave zero when something moved a node out from under React — a branch
 * `lib/translate-dom-guard-script.ts` documents as unreachable for a tree React
 * owns exclusively.
 *
 * ## Why the signal is latched rather than read live
 *
 * The evidence is gone by the time anything asks for it. React recovers from
 * #418 by regenerating the mismatched tree, which discards the translator's
 * `<font>` wrappers, and hydration rewrites `<html>`'s class list, which takes
 * Google Translate's `translated-ltr` marker with it. The probe measured this
 * directly: three seconds after 872 wrapped text nodes produced #418 on `/`, a
 * live DOM read found no marker at all on any of the five routes. A filter that
 * read the DOM inside `beforeSend` would therefore never fire — it would look
 * like a fix and do nothing.
 *
 * So `lib/translate-dom-guard-script.ts` latches a sticky `translated` flag in
 * its pre-hydration inline script, the moment it observes a translator, and
 * that flag (`latch`) is what this module reads first. The three live-DOM
 * markers are kept as a fallback for the cases where they do survive.
 */

/** Marker names, in the order they are tested. */
export const TRANSLATION_SIGNALS = ['latch', 'guard', 'font', 'gt-class', 'mst'] as const

export type TranslationSignal = (typeof TRANSLATION_SIGNALS)[number] | ''

/** Google Translate stamps one of these on `<html>` and leaves it there. */
const GOOGLE_TRANSLATE_ROOT_CLASSES = ['translated-ltr', 'translated-rtl']

/** Microsoft Translator (Edge) stamps this attribute on each element it rewrites. */
const MICROSOFT_TRANSLATE_ATTRIBUTE = '[_msttexthash]'

/**
 * Matches the `window` key `lib/translate-dom-guard-script.ts` publishes its
 * suppression counters on. Kept as a literal rather than imported so this
 * module stays free of the guard's script string.
 */
const GUARD_COUNTERS_KEY = '__tlsTranslateDomGuard'

type ClassListLike = { contains(token: string): boolean }

/** The narrow slice of `Document` this needs, so it can be driven from a test. */
export type TranslationDocumentLike = {
  getElementsByTagName(tagName: string): { length: number }
  querySelector(selectors: string): unknown
  documentElement?: { classList?: ClassListLike | null } | null
}

type GuardCounters = {
  removeChild?: number
  insertBefore?: number
  replaceChild?: number
  translated?: boolean
}

function guardCounters(scope: unknown): GuardCounters | null {
  if (!scope || typeof scope !== 'object') return null
  const counters = (scope as Record<string, unknown>)[GUARD_COUNTERS_KEY]
  if (!counters || typeof counters !== 'object') return null
  return counters as GuardCounters
}

/**
 * The sticky flag `lib/translate-dom-guard-script.ts` latches the first time it
 * observes a translator — either because it suppressed a mutation React could
 * not have asked for, or because its observer saw `<font>` wrappers appear.
 *
 * This is the load-bearing check. The three live-DOM checks below are a
 * fallback for the case where the markers happen to still be on the page; the
 * probe measured them absent three seconds after the error on every route,
 * because React regenerates the mismatched tree and hydration rewrites
 * `<html>`'s class list.
 */
function hasLatchedTranslationMarker(scope: unknown): boolean {
  return guardCounters(scope)?.translated === true
}

function hasSuppressedGuardMutation(scope: unknown): boolean {
  const counters = guardCounters(scope)
  if (!counters) return false
  return (
    (counters.removeChild ?? 0) > 0 ||
    (counters.insertBefore ?? 0) > 0 ||
    (counters.replaceChild ?? 0) > 0
  )
}

/**
 * The first marker present, or `''` when the DOM carries no third-party
 * fingerprint. Never throws: it runs inside `beforeSend`, and a telemetry path
 * that can itself raise is the feedback loop `lib/client-error-census.ts`
 * documents at length.
 */
export function pageTranslationSignal(
  doc: TranslationDocumentLike | null | undefined,
  scope: unknown = typeof globalThis === 'undefined' ? undefined : globalThis,
): TranslationSignal {
  try {
    // Durable evidence first — see hasLatchedTranslationMarker.
    if (hasLatchedTranslationMarker(scope)) return 'latch'
    if (hasSuppressedGuardMutation(scope)) return 'guard'

    if (doc) {
      if (doc.getElementsByTagName('font').length > 0) return 'font'

      const classList = doc.documentElement?.classList
      if (classList && GOOGLE_TRANSLATE_ROOT_CLASSES.some((name) => classList.contains(name))) {
        return 'gt-class'
      }

      if (doc.querySelector(MICROSOFT_TRANSLATE_ATTRIBUTE)) return 'mst'
    }
  } catch {
    // An unreadable DOM is not evidence either way.
  }

  return ''
}

/** Reads the live document, when there is one. */
export function currentPageTranslationSignal(): TranslationSignal {
  if (typeof document === 'undefined') return ''
  return pageTranslationSignal(document)
}

export function isTranslationSignal(value: unknown): value is (typeof TRANSLATION_SIGNALS)[number] {
  return TRANSLATION_SIGNALS.some((signal) => signal === value)
}
